'use client'

import * as m from 'motion/react-m'
import { useTransform, useScroll } from 'motion/react'
import { Motion } from '@/lib/motion/lazy-motion'
import Hero from '@/components/content/common/Hero'
import Container from '@/components/structure/Container'
import AppStack from '@/components/content/app/AppStack'
import EmailSignUp from '@/components/ui/EmailSignUp'
import AppIcon from '@/components/content/app/AppIcon'
import Logo from '@/components/ui/Logo'
import Pill from '@/components/ui/Pill'
import DealClaimButton from '@/components/content/deals/DealClaimButton'
import { Icon } from '@/icons/Icon'
import { FeaturedPlacement, trackFeaturedClick } from '@/lib/analytics'
import { usePageContext } from '@/components/content/common/PageContext'
import { DealEligibility, DealOverview } from '@/components/content/deals/deal/DealSummary'
import { checkAppBranding } from '@/lib/formatting'
import {
  cn,
  hexToRgba,
  scrollToSection,
  getBrandColors,
  sortToFeaturedOrder,
  isPopulatedObject,
} from '@/lib/utils'
import { createLogger } from '@/lib/logger'
import type { App, Deal, Media } from '@/payload-types'

const logger = createLogger('DealsHero')

type DealHeroProps = {
  deals: Deal[]
}

export default function DealsHero({ deals }: DealHeroProps) {
  const { setSelectedFilter } = usePageContext()

  const deal = deals && deals.length > 0 ? sortToFeaturedOrder(deals)[0] : null
  if (!deal) return null
  const app = deal.app as App

  const totalDeals = deals.length
  const totalClaims = deals.reduce((acc, deal) => acc + (deal.claimed ?? 0), 0)

  const { primaryColor, secondaryColor, tertiaryColor, backgroundColor } = getBrandColors(app)
  const { appIcon, appGlyph } = checkAppBranding(app)
  const resolvedIcon = appGlyph ?? appIcon

  const exploreFeatured = () => {
    scrollToSection({ id: 'filters', offset: 0, animate: true, hideHash: true })
    setSelectedFilter({ id: 0, name: 'Featured', slug: 'featured' })
  }

  const gradient = `radial-gradient(70% 100% at 0% 0%, ${hexToRgba(primaryColor, 0.25)} 0%, ${hexToRgba(primaryColor, 0)} 80%),
                  radial-gradient(70% 100% at 100% 0%, ${hexToRgba(secondaryColor, 0.25)} 0%, ${hexToRgba(secondaryColor, 0)} 80%),
                  radial-gradient(100% 100% at 50% 0%, ${hexToRgba(tertiaryColor, 0.25)} 0%, ${hexToRgba(tertiaryColor, 0)} 80%, rgba(0, 0, 0, 0) 100%),
                  #000`

  return (
    <Hero animation={{ scale: true }} center={false} className="bg-gradient pt-16">
      {({ animations }) => (
        <Motion>
          <>
            <Container
              reduced={true}
              className={cn(
                'relative z-20 flex h-full w-full flex-col items-center justify-center gap-6 md:flex-row md:gap-10',
              )}
            >
              <m.div
                style={{ opacity: animations?.opacity, y: animations?.y }}
                className="flex w-full flex-col gap-8"
              >
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col gap-2 md:gap-6">
                    <h1>Software Deals</h1>
                    <p className="text-14 leading-normal text-white/60 md:text-18">
                      Discover unbeatable deals.
                      <br />
                      Explore, claim, and save effortlessly.
                    </p>
                  </div>
                  <div className="flex items-center gap-3 md:flex-col md:items-start lg:flex-row lg:items-center">
                    <DealCounter deals={totalDeals} claims={totalClaims} />
                    <div
                      onClick={exploreFeatured}
                      className="hidden h-8 cursor-pointer items-center px-3.5 font-medium text-white transition-colors hover:text-primary-500 xs:flex"
                    >
                      Explore featured deals
                    </div>
                  </div>
                  <EmailSignUp focus="New Deals" theme="dark" maxWidth={480} />
                </div>
              </m.div>

              <m.div
                className="relative flex max-h-[560px] w-full flex-col overflow-hidden rounded-3xl border border-white/10 p-4 md:h-[calc(100%-96px)] md:max-w-[420px] md:p-6"
                style={{
                  opacity: animations?.opacity,
                  scale: animations?.scale,
                  background: gradient,
                }}
              >
                <AppStack
                  icon={appIcon}
                  glyph={appGlyph}
                  multiple={true}
                  name={app.name}
                  background={backgroundColor}
                  size="lg"
                  invert={true}
                  className="absolute -right-10 -top-4 -rotate-[15deg] scale-50 md:right-6 md:top-16 md:scale-125"
                />

                <FeaturedDeal />

                <div className="relative z-10 mt-3 flex flex-col gap-3 md:mt-auto md:gap-6">
                  <span className="font-heading text-36 font-bold">{app.name}</span>
                  <div className="flex w-full flex-col gap-1.5">
                    <DealOverview deal={deal} />
                    <DealEligibility eligibility={deal.eligibility} terms={deal.terms} />
                  </div>
                  <DealClaimButton
                    deal={deal}
                    app={app}
                    onClick={() => trackFeaturedClick(FeaturedPlacement.DEALS_HERO, app.name)}
                  >
                    <Pill
                      size="large"
                      style="gradient"
                      icon={true}
                      chevron={true}
                      className="w-full"
                    >
                      <AppIcon
                        icon={appIcon}
                        glyph={appGlyph}
                        name={app.name}
                        background={backgroundColor}
                        size={32}
                        circular={true}
                      />
                      <span className="w-full text-center">Claim {app.name} Deal</span>
                    </Pill>
                  </DealClaimButton>
                </div>
              </m.div>
            </Container>

            {resolvedIcon && <BackgroundGlyph icon={resolvedIcon} name={app.name} />}
          </>
        </Motion>
      )}
    </Hero>
  )
}

export function DealCounter({ deals, claims }: { deals: number; claims: number }) {
  return (
    // `meticulous-ignore` + `data-review="ignore"`: both the deal count and the
    // claim count drift between Meticulous runs (claims tick up live; deals
    // change as admin publishes/archives). Wrapping the whole counter — not
    // just the claims half — prevents the entire pill from being a permanent
    // visual diff on every audit.
    <div
      data-review="ignore"
      className="meticulous-ignore flex w-fit gap-2 rounded-full border border-white/15 bg-white/5 py-1 pl-1 pr-3.5"
    >
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-white">
        <Icon name="ticket" />
      </div>
      <div className="flex items-center gap-2 font-medium text-white">
        <span>{deals} deals</span>
        <span className="opacity-50">{claims} claims</span>
      </div>
    </div>
  )
}

export function FeaturedDeal() {
  return (
    // `meticulous-ignore` on the whole pill — its `animate-ping` dot + `animate-shine`
    // text gradient both loop forever, so screenshot timing between Meticulous runs
    // catches different frames and produces false-positive visual diffs. The pill
    // is small and self-contained — ignoring it doesn't affect the rest of the hero.
    <div
      data-review="ignore"
      className="meticulous-ignore flex h-[36px] w-fit cursor-default items-center gap-3 rounded-full border border-white/25 bg-black pl-3.5 pr-4 text-14"
    >
      <div className="relative flex h-2 w-2 items-center justify-center rounded-full">
        <span className="absolute h-full w-full rounded-full bg-primary-500"></span>
        <span className="absolute h-3.5 w-3.5 animate-ping rounded-full bg-primary-500 opacity-50"></span>
      </div>

      <div className="relative animate-shine bg-gradient-to-r from-white/40 via-white to-white/40 bg-[length:200%_100%] bg-clip-text text-transparent">
        Featured Deal
      </div>
    </div>
  )
}

export function BackgroundGlyph({ icon, name }: { icon: Media; name: string }) {
  const height = 680
  const { scrollY } = useScroll()

  const scrollDown = useTransform(scrollY, [0, height], [0, 50])

  if (!isPopulatedObject<Media>(icon)) {
    logger.error('App icon should not be a number in BackgroundGlyph')
    return null
  }

  return (
    <Motion>
      <m.div
        style={{
          y: scrollDown,
          WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0) 100%)',
          maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0) 100%)',
        }}
        className="absolute -bottom-16 -right-24 z-0"
      >
        <Logo icon={icon} alt={name} size={440} />
      </m.div>
    </Motion>
  )
}
